import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { APP_NAME, LOGO_MARK } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Loader2, ShieldCheck, BarChart3, Wallet } from "lucide-react";

export default function Login() {
  const { user, login, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden border-r border-white/5">
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg flex items-center justify-center overflow-hidden bg-white/[0.03] ring-1 ring-brand/30 shadow-glow-cyan">
              <img src={LOGO_MARK} alt="Ethara.AI" className="h-7 w-7 object-contain" />
            </div>
            <span className="font-heading font-bold text-xl tracking-tight">
              Ethara<span className="text-brand text-glow-cyan">.AI</span>
            </span>
          </div>
        </div>
        <div className="relative z-10 space-y-8">
          <h1 className="font-heading text-4xl xl:text-5xl leading-[1.05] tracking-tight font-bold">
            AI-native financial
            <br /> <span className="text-brand text-glow-cyan">intelligence.</span>
          </h1>
          <p className="text-muted-foreground text-base max-w-md leading-relaxed">
            Bank balances, payables, ARR, fixed investments and FIRCs — unified into a live
            P&amp;L dashboard with AI-written MIS for your board.
          </p>
          <div className="space-y-3 pt-2">
            {[
              { icon: BarChart3, t: "Real-time P&L, ratios & comparisons" },
              { icon: Wallet, t: "Multi-currency: INR & USD" },
              { icon: ShieldCheck, t: "Month-wise AI investor insights" },
            ].map((f) => (
              <div key={f.t} className="flex items-center gap-3 text-foreground/85">
                <div className="h-8 w-8 rounded-md bg-white/[0.03] border border-white/10 flex items-center justify-center">
                  <f.icon className="h-[16px] w-[16px] text-brand" />
                </div>
                <span className="text-sm">{f.t}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-xs text-muted-foreground">
          © 2026 {APP_NAME} · Management Information System
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm glass-panel rounded-2xl p-8">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="h-10 w-10 rounded-lg flex items-center justify-center overflow-hidden bg-white/[0.03] ring-1 ring-brand/30 shadow-glow-cyan">
              <img src={LOGO_MARK} alt="Ethara.AI" className="h-6 w-6 object-contain" />
            </div>
            <span className="font-heading font-bold text-lg">
              Ethara<span className="text-brand text-glow-cyan">.AI</span>
            </span>
          </div>
          <h2 className="font-heading text-2xl font-semibold tracking-tight">Welcome back</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-6">Sign in to access your financial MIS.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com" className="mt-1.5" data-testid="email-input" required />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" className="mt-1.5" data-testid="password-input" required />
            </div>
            {error && (
              <p className="text-sm text-danger bg-danger/10 border border-danger/20 rounded-md px-3 py-2" data-testid="auth-error">
                {error}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Sign In
            </Button>
          </form>

          <Card className="mt-6 p-3 bg-muted/50 border-dashed border-border">
            <p className="text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">Ethara team access.</span>
              <br /> Sign in with your Ethara finance account. Super admin &amp; Finance share one company workspace.
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}
